// store.js
import { configureStore, createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import ApiService from '../apiClient';

// =======================================================
// 1️⃣ Generic Async Thunks Factory
// =======================================================

// Fetch all items for a resource
export const createFetchAll = (resource) =>
  createAsyncThunk(
    `${resource}/fetchAll`,
    async ({ parent = null, needsAuth = false } = {}, { rejectWithValue }) => {
      try {
        const fnName = `get_${resource}`;

        // If specific function exists (example: get_tournament)
        if (typeof ApiService[fnName] === "function") {
          return await ApiService[fnName](parent, needsAuth);
        }

        // Fallback to generic getAll
        if (typeof ApiService.getAll === "function") {
          return await ApiService.getAll(resource, parent, needsAuth);
        }

        throw new Error(`No API method found to fetch ${resource}`);
      } catch (err) {
        return rejectWithValue(
          err.message || `Failed to fetch ${resource}`
        );
      }
    }
  );

export const fetchContentById = (id, needsAuth) => async (dispatch) => {
  dispatch(contentLoading());
  try {
    const response = await ApiService.getOne("content", id, needsAuth);
    console.log("apiSlice==RESOURCE BY ID====", response);
    dispatch(contentLoaded(response));
  } catch (error) {
    dispatch(contentError(error.message));
  }
};

// Fetch a single item by ID
export const createFetchById = (resource) =>
  createAsyncThunk(
    `${resource}/fetchById`,
    async ({ id, parent = null, needsAuth = false }, { rejectWithValue }) => {
      try {
        console.log("resource=====", resource)
        if (!id) throw new Error("id is required");

        const fnName = `get_${resource}_by_id`;

        // 🔹 If specific function exists
        if (typeof ApiService[fnName] === "function") {
          return await ApiService[fnName](id, parent, needsAuth);
        }
        console.log("parent=====", parent)
        // 🔹 Fallback to generic getOne
        if (typeof ApiService.getOne === "function") {
          return await ApiService.getOne(resource, id, parent, needsAuth);
        }

        throw new Error(`No API method found to fetch ${resource} by id`);
      } catch (err) {
        return rejectWithValue(
          err.message || `Failed to fetch ${resource}`
        );
      }
    }
  );


// Create an item
export const createCreateItem = (resource) =>
  createAsyncThunk(`${resource}/createItem`, async ({ data, parent, needsAuth = false }, { rejectWithValue }) => {
    try {
      if (!data) throw new Error("data is required");

      // Use resource-specific API if exists
      const fnName = `create_${resource}`;
      if (typeof ApiService[fnName] === 'function') {
        return await ApiService[fnName](resource, data, needsAuth, parent);
      }

      // fallback to generic API
      if (typeof ApiService.createItem === 'function') {
        return await ApiService.createItem(resource, data, needsAuth, parent);
      }

      throw new Error(`No API method found to create ${resource}`);
    } catch (err) {
      return rejectWithValue(err.message || `Failed to create ${resource}`);
    }
  });

// Update an item
export const createUpdateItem = (resource) =>
  createAsyncThunk(`${resource}/updateItem`, async ({ id, data, parent, needsAuth = false }, { rejectWithValue }) => {
    try {
      if (!id || !data) throw new Error("id and data are required");

      const fnName = `update_${resource}`;
      if (typeof ApiService[fnName] === 'function') {
        return await ApiService[fnName](id, data, parent, needsAuth);
      }

      if (typeof ApiService.updateItem === 'function') {
        return await ApiService.updateItem(resource, id, data, parent, needsAuth);
      }

      throw new Error(`No API method found to update ${resource}`);
    } catch (err) {
      return rejectWithValue(err.message || `Failed to update ${resource}`);
    }
  });

// Delete an item
export const createDeleteItem = (resource) =>
  createAsyncThunk(`${resource}/deleteItem`, async ({ id, needsAuth = false }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("id is required");

      const fnName = `delete_${resource}`;
      if (typeof ApiService[fnName] === 'function') {
        await ApiService[fnName](id, needsAuth);
        return id;
      }

      if (typeof ApiService.deleteItem === 'function') {
        await ApiService.deleteItem(resource, id, needsAuth);
        return id;
      }

      throw new Error(`No API method found to delete ${resource}`);
    } catch (err) {
      return rejectWithValue(err.message || `Failed to delete ${resource}`);
    }
  });

// =======================================================
// 2️⃣ Generic Slice Factory
// =======================================================

export const createGenericSlice = (name, asyncThunks = {}, isArray = true) => {
  return createSlice({
    name,
    initialState: {
      data: isArray ? [] : null,
      status: 'idle',
      error: null,
    },
    reducers: {
      clearState(state) {
        state.data = isArray ? [] : null;
        state.status = 'idle';
        state.error = null;
      },
    },
    extraReducers: (builder) => {
      Object.values(asyncThunks).forEach((thunk) => {
        builder
          .addCase(thunk.pending, (state) => { state.status = 'loading'; })
          .addCase(thunk.fulfilled, (state, action) => { state.status = 'succeeded'; state.data = action.payload; })
          .addCase(thunk.rejected, (state, action) => { state.status = 'failed'; state.error = action.error?.message; });
      });
    },
  });
};

// =======================================================
// 3️⃣ Helper to get generic async actions
// =======================================================

export const getAsyncActions = (resource) => ({
  fetchAll: createFetchAll(resource),
  fetchById: createFetchById(resource),
  createItem: createCreateItem(resource),
  updateItem: createUpdateItem(resource),
  deleteItem: createDeleteItem(resource),
});

// =======================================================
// 4️⃣ Custom / Resource-Specific Thunks
// =======================================================

export const fetchTournamentsByProgramId = createAsyncThunk(
  'tournament/fetchByProgramId',
  async ({ programId, needsAuth = false }) => {
    if (!programId) throw new Error("programId is required");

    // Call the generic API for nested resource
    return await ApiService.getAll('tournament', needsAuth, { type: 'program', id: programId });
  }
);


export const fetchTournamentInfo = createAsyncThunk(
  'tournamentInfo/fetch',
  async ({ programId, tournamentId, needsAuth = false }) => {
    if (!programId) throw new Error("programId is required");
    if (!tournamentId) throw new Error("tournamentId is required");

    const parent = { type: 'program', id: programId };
    console.log("parent=====================", parent)
    // Use generic getOne for nested resource: tournamentInfo under program/tournament
    return await ApiService.getOne(
      'tournament',       // resource name
      tournamentId,
      parent,
      needsAuth
    );
  }
);

export const updateTournamentInfo = createAsyncThunk(
  'tournamentInfo/update',
  async ({ programId, tournamentId, data, needsAuth = false }) => {
    // Use parent as program
    const parent = { type: 'program', id: programId };
    const infoId = data.id; // the actual TournamentInfo id

    return await ApiService.updateItem(
      'tournament-edit', // resource name
      tournamentId,           // ID of the TournamentInfo
      data,
      parent,
      needsAuth
    );
  }
);

// =======================================================
// 5️⃣ Create Slices
// =======================================================

const introSlice = createGenericSlice('intro', getAsyncActions('intro'));
const contentSlice = createGenericSlice('content', getAsyncActions('content'));
const programSlice = createGenericSlice('program', getAsyncActions('program'));
const labSlice = createGenericSlice('lab', getAsyncActions('lab'));

const tournamentSlice = createGenericSlice('tournament', {
  ...getAsyncActions('tournament'),
  fetchByProgramId: fetchTournamentsByProgramId, // custom
});

const tournamentInfoSlice = createGenericSlice(
  'tournamentInfo',
  {
    fetch: fetchTournamentInfo,
    update: updateTournamentInfo,
  },
  false // single object
);

// =======================================================
// 6️⃣ Configure Store
// =======================================================

const store = configureStore({
  reducer: {
    intro: introSlice.reducer,
    content: contentSlice.reducer,
    program: programSlice.reducer,
    lab: labSlice.reducer,
    tournament: tournamentSlice.reducer,
    tournamentInfo: tournamentInfoSlice.reducer,
  },
});

export default store;

// =======================================================
// 7️⃣ Export Sync Actions
// =======================================================

export const introActions = introSlice.actions;
export const contentActions = contentSlice.actions;
export const programActions = programSlice.actions;
export const labActions = labSlice.actions;
export const tournamentActions = tournamentSlice.actions;
export const tournamentInfoActions = tournamentInfoSlice.actions;

// =======================================================
// 8️⃣ Export Async Actions
// =======================================================

export const introExtraActions = getAsyncActions('intro');
export const contentExtraActions = getAsyncActions('content');
export const programExtraActions = getAsyncActions('program');
export const labExtraActions = getAsyncActions('lab');

export const tournamentExtraActions = {
  ...getAsyncActions('tournament'),  // includes createItem
  fetchByProgramId: fetchTournamentsByProgramId,
};

export const tournamentInfoExtraActions = {
  fetchTournamentInfo,
  updateTournamentInfo,
};
